import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

const PHI = 1.6180339887;

/**
 * Golden Rectangle adapter.
 *
 * Inscribes a true golden rectangle (φ:1) inside the element,
 * then recursively subdivides it into squares. Because each
 * remaining rectangle is also a golden rectangle, the quarter-circle
 * arcs in every square connect seamlessly into the golden spiral.
 */
export class GoldenRectangleAdapter extends GridAdapter {
  readonly type = "golden-rectangle";
  readonly label = "Golden";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="10" height="10" rx="3" fill="currentColor"/><path d="M9 13V3" stroke="white"/><path d="M13 9H9" stroke="white"/></svg>`;

  getDefaults(): Record<string, any> {
    return {
      depth: 8,
      showSpiral: true,
      color: "#e2a44a",
      opacity: 0.4,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      { type: "number", key: "depth", label: "Depth", default: 8, min: 2, max: 14, step: 1 },
      { type: "toggle", key: "showSpiral", label: "Show spiral", default: true },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#e2a44a" },
      { type: "range", key: "opacity", label: "Opacity", default: 0.4, min: 0, max: 1, step: 0.01 },
    ];
  }

  render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { depth, showSpiral, color, opacity } = settings;

    // ── Inscribe a golden rectangle (φ : 1) centered in the element ──
    let gw: number, gh: number;
    if (width / height >= PHI) {
      gh = height;
      gw = gh * PHI;
    } else {
      gw = width;
      gh = gw / PHI;
    }
    const gx = (width - gw) / 2;
    const gy = (height - gh) / 2;

    // Draw the golden rectangle border if it doesn't fill the element
    if (gw < width - 1 || gh < height - 1) {
      const border = this.createPath(
        `M ${gx} ${gy} L ${gx + gw} ${gy} L ${gx + gw} ${gy + gh} L ${gx} ${gy + gh} Z`,
        color, opacity * 0.3, 1,
      );
      border.setAttribute("stroke-dasharray", "6 4");
      group.appendChild(border);
    }

    // ── Subdivide into squares, rotating CW: right → bottom → left → top ──
    let x = gx, y = gy, w = gw, h = gh;
    let spiralD = "";
    let isFirst = true;

    for (let i = 0; i < depth; i++) {
      if (w < 1 || h < 1) break;
      const dir = i % 4;
      const s = Math.min(w, h);
      let sqX: number, sqY: number;

      switch (dir) {
        case 0: {
          // Square on RIGHT
          sqX = x + w - s;
          sqY = y;
          if (w - s > 0.5) group.appendChild(this.createLine(sqX, y, sqX, y + h, color, opacity));

          if (showSpiral) {
            // Center: top-left of square. Arc: TR → BL (CW)
            if (isFirst) { spiralD = `M ${sqX + s} ${sqY}`; isFirst = false; }
            spiralD += ` A ${s} ${s} 0 0 1 ${sqX} ${sqY + s}`;
          }

          w -= s;
          break;
        }
        case 1: {
          // Square on BOTTOM
          sqX = x;
          sqY = y + h - s;
          if (h - s > 0.5) group.appendChild(this.createLine(x, sqY, x + w, sqY, color, opacity));

          if (showSpiral) {
            // Center: top-right of square. Arc: BR → TL (CW)
            if (isFirst) { spiralD = `M ${sqX + s} ${sqY + s}`; isFirst = false; }
            spiralD += ` A ${s} ${s} 0 0 1 ${sqX} ${sqY}`;
          }

          h -= s;
          break;
        }
        case 2: {
          // Square on LEFT
          sqX = x;
          sqY = y;
          if (w - s > 0.5) group.appendChild(this.createLine(x + s, y, x + s, y + h, color, opacity));

          if (showSpiral) {
            // Center: bottom-right of square. Arc: BL → TR (CW)
            if (isFirst) { spiralD = `M ${sqX} ${sqY + s}`; isFirst = false; }
            spiralD += ` A ${s} ${s} 0 0 1 ${sqX + s} ${sqY}`;
          }

          x += s;
          w -= s;
          break;
        }
        case 3: {
          // Square on TOP
          sqX = x;
          sqY = y;
          if (h - s > 0.5) group.appendChild(this.createLine(x, y + s, x + w, y + s, color, opacity));

          if (showSpiral) {
            // Center: bottom-left of square. Arc: TL → BR (CW)
            if (isFirst) { spiralD = `M ${sqX} ${sqY}`; isFirst = false; }
            spiralD += ` A ${s} ${s} 0 0 1 ${sqX + s} ${sqY + s}`;
          }

          y += s;
          h -= s;
          break;
        }
      }
    }

    if (showSpiral && spiralD) {
      group.appendChild(this.createPath(spiralD, color, opacity * 0.8, 1.5));
    }
  }
}
